import { z } from "zod";

import {
  type CheckoutEntitlementGrant,
  type CheckoutPlanId,
  checkoutFeaturesForPlan,
} from "./checkout-contract";
import {
  type CheckoutProviderPurchaseRef,
  projectCheckoutEntitlementState,
} from "./checkout-entitlements";
import {
  CURRENT_ENTITLEMENT_STATE,
  type EntitlementState,
  PAID_FEATURES,
  type PaidFeatureId,
} from "./paid-features";

export const CHECKOUT_ENTITLEMENT_SNAPSHOT_SCHEMA = "invoice-builder.checkout-entitlements.v1";

export interface CheckoutEntitlementSnapshot {
  schema: typeof CHECKOUT_ENTITLEMENT_SNAPSHOT_SCHEMA;
  source: "verified_checkout";
  metadataPolicy: "no_document_contents";
  verifiedAt: string;
  grants: CheckoutEntitlementGrant[];
  revokedProviderPurchases: CheckoutProviderPurchaseRef[];
}

export type CheckoutEntitlementSnapshotRejectReason =
  | "invalid_snapshot_shape"
  | "future_verified_at"
  | "invalid_now";

export type CheckoutEntitlementSnapshotResult =
  | {
      ok: true;
      state: EntitlementState;
      snapshot: CheckoutEntitlementSnapshot;
      hasActiveEntitlement: boolean;
    }
  | {
      ok: false;
      state: EntitlementState;
      reason: CheckoutEntitlementSnapshotRejectReason;
    };

const paidFeatureIds = Object.keys(PAID_FEATURES) as PaidFeatureId[];

const checkoutFeatureSchema = z
  .string()
  .refine((feature): feature is PaidFeatureId => paidFeatureIds.includes(feature as PaidFeatureId));

const dateStringSchema = z
  .string()
  .min(1)
  .refine((value) => !Number.isNaN(Date.parse(value)));

const redactedProviderReferenceSchema = z.string().regex(/^(?:sha256:)?[a-f0-9]{32,128}$/i);

const checkoutGrantSchema = z
  .object({
    plan: z.enum(["monthly", "lifetime"]),
    source: z.literal("verified_checkout"),
    provider: z.string().min(1),
    providerCustomerId: redactedProviderReferenceSchema,
    providerPurchaseId: redactedProviderReferenceSchema,
    features: z.array(checkoutFeatureSchema).min(1),
    issuedAt: dateStringSchema,
    expiresAt: dateStringSchema.optional(),
  })
  .strict()
  .superRefine((grant, ctx) => {
    const expectedFeatures = checkoutFeaturesForPlan(grant.plan as CheckoutPlanId);
    if (!sameFeatureSet(grant.features as PaidFeatureId[], expectedFeatures)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "features must match the canonical paid feature set for the plan",
        path: ["features"],
      });
    }

    if (grant.plan === "monthly" && !grant.expiresAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "monthly grants require provider-verified period end",
        path: ["expiresAt"],
      });
    }

    if (grant.plan === "lifetime" && grant.expiresAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "lifetime grants must not include expiresAt",
        path: ["expiresAt"],
      });
    }
  });

const checkoutProviderPurchaseRefSchema = z
  .object({
    provider: z.string().min(1),
    providerPurchaseId: redactedProviderReferenceSchema,
  })
  .strict();

const checkoutEntitlementSnapshotSchema = z
  .object({
    schema: z.literal(CHECKOUT_ENTITLEMENT_SNAPSHOT_SCHEMA),
    source: z.literal("verified_checkout"),
    metadataPolicy: z.literal("no_document_contents"),
    verifiedAt: dateStringSchema,
    grants: z.array(checkoutGrantSchema),
    revokedProviderPurchases: z.array(checkoutProviderPurchaseRefSchema).default([]),
  })
  .strict();

export function parseCheckoutEntitlementSnapshotPayload(
  payload: unknown,
  now = new Date().toISOString(),
): CheckoutEntitlementSnapshotResult {
  if (Number.isNaN(Date.parse(now))) {
    return {
      ok: false,
      state: CURRENT_ENTITLEMENT_STATE,
      reason: "invalid_now",
    };
  }

  const parsed = checkoutEntitlementSnapshotSchema.safeParse(payload);
  if (!parsed.success) {
    return {
      ok: false,
      state: CURRENT_ENTITLEMENT_STATE,
      reason: "invalid_snapshot_shape",
    };
  }

  if (Date.parse(parsed.data.verifiedAt) > Date.parse(now)) {
    return {
      ok: false,
      state: CURRENT_ENTITLEMENT_STATE,
      reason: "future_verified_at",
    };
  }

  const snapshot: CheckoutEntitlementSnapshot = {
    ...parsed.data,
    grants: parsed.data.grants.map((grant) => ({
      ...grant,
      plan: grant.plan as CheckoutPlanId,
      features: grant.features as PaidFeatureId[],
    })),
    revokedProviderPurchases: parsed.data.revokedProviderPurchases,
  };

  const state = projectCheckoutEntitlementState({
    grants: snapshot.grants,
    revokedProviderPurchases: snapshot.revokedProviderPurchases,
    now,
  });

  return {
    ok: true,
    snapshot,
    state,
    hasActiveEntitlement: state.source === "verified_checkout",
  };
}

function sameFeatureSet(actual: PaidFeatureId[], expected: PaidFeatureId[]): boolean {
  if (actual.length !== expected.length) return false;
  const expectedSet = new Set(expected);
  return actual.every((feature) => expectedSet.has(feature));
}
